import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  XCircle,
  FileText,
  CheckCircle,
  X,
  ExternalLink,
  Loader2,
  Upload
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import offerService from '../../../api/services/offerService';
import apiClient, { post, del } from '../../../api/apiClient';
import { API_ENDPOINTS } from '../../../api/config';
import { useCustomAlert } from '../../../hooks/useCustomAlert';

const EditOffer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [offer, setOffer] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [salaryStructures, setSalaryStructures] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingSalaryStructures, setLoadingSalaryStructures] = useState(false);

  // Document management state
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [newDocuments, setNewDocuments] = useState([]);
  const [documentsToDelete, setDocumentsToDelete] = useState([]);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isUploading, setIsUploading] = useState(false);

  const { showSuccess, showError } = useCustomAlert();

  const [formData, setFormData] = useState({
    offerType: 'full-time',
    jobTitle: '',
    department: '',
    location: '',
    salary: '',
    currency: 'INR',
    startDate: '',
    expiryDate: '',
    probationPeriod: 6,
    noticePeriod: 30,
    notes: '',
    benefits: [],
    templateId: '',
    terms: {
      workingHours: ''
    }
  });


  // Helpers for date limits
  const getTodayISO = () => new Date().toISOString().split('T')[0];
  const getISOAfterYears = (years) => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + years);
    return d.toISOString().split('T')[0];
  };
  const MAX_YEARS_AHEAD = 5;

  const offerTypes = [
    { value: 'full-time', label: 'Full Time' },
    { value: 'part-time', label: 'Part Time' },
    { value: 'contract', label: 'Contract' },
    { value: 'internship', label: 'Internship' }
  ];

  const currencies = [
    { id: 'INR', name: 'Indian Rupee (₹)' },
    { id: 'USD', name: 'US Dollar ($)' },
    { id: 'EUR', name: 'Euro (€)' }
  ];

  const [workingHoursOptions, setWorkingHoursOptions] = useState([]);
  const [loadingWorkingHours, setLoadingWorkingHours] = useState(false);

  const hasCurrentWorkingHoursOption = useMemo(() => {
    const current = formData.terms.workingHours?.toString().trim().toLowerCase();
    if (!current) return true;
    return workingHoursOptions.some(option => {
      const optionValue = option.value?.toString().trim().toLowerCase();
      const optionLabel = option.label?.toString().trim().toLowerCase();
      return optionValue === current || optionLabel === current;
    });
  }, [workingHoursOptions, formData.terms.workingHours]);

  const hasCurrentJobTitleOption = useMemo(() => {
    const current = formData.jobTitle?.toString().trim();
    if (!current) return true;
    return jobs.some(job => {
      const jobValue = job.value?.toString().trim();
      const jobLabel = job.label?.toString().trim();
      return jobValue === current || jobLabel === current;
    });
  }, [jobs, formData.jobTitle]);

  const hasCurrentSalaryOption = useMemo(() => {
    const current = formData.salary?.toString().trim();
    if (!current) return true;
    return salaryStructures.some(structure => {
      const structureValue = structure.value?.toString().trim();
      return structureValue === current;
    });
  }, [salaryStructures, formData.salary]);

  const availableBenefits = [
    'Health Insurance',
    'Provident Fund',
    'Annual Bonus',
    'Professional Development',
    'Flexible Working Hours',
    'Work From Home',
    'Gym Membership',
    'Transport Allowance',
    'Meal Allowance',
    'Performance Bonus'
  ];

  const templates = [
    {
      id: 'standard',
      name: 'Standard Offer',
      department: 'All',
      salaryRange: '500000-1500000',
      benefits: ['Health Insurance', 'Provident Fund', 'Annual Bonus'],
      content: `We are pleased to offer you the position of {jobTitle} at ${process.env.REACT_APP_COMPANY_NAME}...`,
      isActive: true
    },
    {
      id: 'engineering',
      name: 'Engineering Offer',
      department: 'Engineering',
      salaryRange: '800000-1500000',
      benefits: ['Health Insurance', 'Provident Fund', 'Annual Bonus', 'Professional Development'],
      content: `We are pleased to offer you the position of {jobTitle} at ${process.env.REACT_APP_COMPANY_NAME}...`,
      isActive: true
    },
    {
      id: 'hr',
      name: 'HR Offer',
      department: 'Human Resources',
      salaryRange: '600000-1200000',
      benefits: ['Health Insurance', 'Provident Fund', 'Annual Bonus', 'Professional Development'],
      content: `We are pleased to offer you the position of {jobTitle} at ${process.env.REACT_APP_COMPANY_NAME}...`,
      isActive: true
    }
  ];

  useEffect(() => {
    const fetchOffer = async () => {
      try {
        setLoading(true);

        // Auth token check

        const result = await offerService.getOfferById(id);


        if (result && result.responseCode === 200 && result.data) {
          // Standard API response format
          setOffer(result.data);
          populateFormData(result.data);
        } else if (result && result.id && result.offerId) {
          // Direct data format (no wrapper)
          setOffer(result);
          populateFormData(result);
        } else {
          showError(result?.responseMessage || 'Failed to fetch offer');
          navigate('/offer_management');
        }
      } catch (err) {

        showError('Failed to fetch offer details');
        navigate('/offer_management');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchOffer();
    }
  }, [id, navigate, showError]);

  // Load departments from backend
  const loadDepartments = async () => {
    try {
      setLoadingDepartments(true);


      const response = await apiClient.get('/hr/departments');

      if (Array.isArray(response.data)) {
        // Direct array format (most likely based on console logs)
        const departmentsData = response.data.map(dept => ({
          value: dept.name,
          label: dept.name,
          id: dept.id || dept._id
        }));
        setDepartments(departmentsData);

      } else if (response.data && response.data.responseCode === 200 && response.data.data) {
        // Standard API response format
        const departmentsData = response.data.data.map(dept => ({
          value: dept.name,
          label: dept.name,
          id: dept.id
        }));
        setDepartments(departmentsData);

      } else {

        setDepartments([]);
      }
    } catch (error) {

      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  // Load locations from backend
  const loadLocations = async () => {
    try {
      setLoadingLocations(true);


      const response = await apiClient.get('/locations/active');

      if (Array.isArray(response.data)) {
        // Direct array format (most likely based on console logs)
        const locationsData = response.data.map(location => ({
          value: location.value || location.name,
          label: location.label || location.name,
          code: location.code,
          city: location.city,
          state: location.state
        }));
        setLocations(locationsData);

      } else if (response.data && response.data.responseCode === 200 && response.data.data) {
        // Standard API response format
        const locationsData = response.data.data.map(location => ({
          value: location.value || location.name,
          label: location.label || location.name,
          code: location.code,
          city: location.city,
          state: location.state
        }));
        setLocations(locationsData);

      } else {

        setLocations([]);
      }

    } catch (error) {

      setLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  };

  const loadWorkingHours = async () => {
    try {
      setLoadingWorkingHours(true);


      const response = await apiClient.get('/shifts');

      let shiftsData = [];
      if (Array.isArray(response.data)) {
        shiftsData = response.data;
      } else if (response.data?.data) {
        shiftsData = Array.isArray(response.data.data) ? response.data.data : [];
      } else if (response.data?.shifts) {
        shiftsData = Array.isArray(response.data.shifts) ? response.data.shifts : [];
      }

      const activeShifts = shiftsData.filter(shift => {
        const status = typeof shift.status === 'string' ? shift.status.toLowerCase() : '';
        return shift.isActive === true || shift.active === true || status === 'active';
      });

      const formattedShifts = activeShifts
        .map(shift => {
          const start =
            shift.startTime ||
            shift.start_time ||
            shift.start ||
            shift.shiftStart ||
            shift.startHour ||
            '';
          const end =
            shift.endTime ||
            shift.end_time ||
            shift.end ||
            shift.shiftEnd ||
            shift.endHour ||
            '';
          const name = shift.name || shift.shiftName || shift.title || shift.label || '';
          const range = start && end ? `${start} - ${end}` : (shift.workingHours || '');
          const label = range ? (name ? `${name} (${range})` : range) : (name || 'Shift');
          const value = shift.workingHours || range || name;

          if (!value) {
            return null;
          }

          return {
            id: shift._id || shift.id || value,
            value,
            label
          };
        })
        .filter(Boolean);

      setWorkingHoursOptions(formattedShifts);

    } catch (error) {

      setWorkingHoursOptions([]);
    } finally {
      setLoadingWorkingHours(false);
    }
  };

  // Load jobs from backend
  const loadJobs = async () => {
    try {
      setLoadingJobs(true);


      // Request with higher limit to get all active jobs
      const response = await apiClient.get('/jobs?status=active&limit=100');

      let jobsData = [];

      // Handle different response formats
      if (Array.isArray(response.data)) {
        // Direct array format
        jobsData = response.data;
      } else if (response.data && response.data.responseCode === 200) {
        // Standard API response format with responseCode
        if (response.data.data) {
          if (Array.isArray(response.data.data)) {
            jobsData = response.data.data;
          } else if (response.data.data.jobs && Array.isArray(response.data.data.jobs)) {
            // Paginated format: { data: { jobs: [...], pagination: {...} } }
            jobsData = response.data.data.jobs;
          }
        }
      } else if (response.data && response.data.data) {
        // Nested data format
        if (Array.isArray(response.data.data)) {
          jobsData = response.data.data;
        } else if (response.data.data.jobs && Array.isArray(response.data.data.jobs)) {
          // Paginated format: { data: { jobs: [...], pagination: {...} } }
          jobsData = response.data.data.jobs;
        }
      } else if (response.data && response.data.jobs && Array.isArray(response.data.jobs)) {
        // Direct paginated format: { jobs: [...], pagination: {...} }
        jobsData = response.data.jobs;
      }



      // Filter only active jobs and map to dropdown format
      const activeJobs = jobsData
        .filter(job => {
          const status = job.status?.toLowerCase();
          const isActive = job.isActive !== false; // Default to true if not specified
          return (status === 'active' || status === 'published') && isActive;
        })
        .map(job => ({
          id: job._id || job.id,
          value: job.title || job.jobTitle || '',
          label: job.title || job.jobTitle || '',
          department: job.department || '',
          location: job.location || ''
        }))
        .filter(job => job.value); // Remove jobs without titles

      setJobs(activeJobs);

    } catch (error) {

      setJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  // Load salary structures from backend
  const loadSalaryStructures = async () => {
    try {
      setLoadingSalaryStructures(true);


      const response = await apiClient.get('/payroll/salary-structures');

      let structuresData = [];
      if (Array.isArray(response.data)) {
        // Direct array format
        structuresData = response.data;
      } else if (response.data && response.data.responseCode === 200) {
        // Standard API response format
        if (response.data.data && Array.isArray(response.data.data)) {
          structuresData = response.data.data;
        } else if (response.data.data && response.data.data.structures) {
          structuresData = response.data.data.structures;
        }
      } else if (response.data && response.data.structures) {
        // Nested structures format
        structuresData = Array.isArray(response.data.structures) ? response.data.structures : [];
      }

      // Map to dropdown format
      const formattedStructures = structuresData
        .map(structure => {
          const salary = structure.ctc || structure.salary || structure.totalCTC || 0;
          const name = structure.name || structure.code || 'Salary Structure';
          const currency = structure.currency || 'INR';
          // Truncate name if too long (max 40 chars) to prevent overflow
          const shortName = name.length > 40 ? name.substring(0, 37) + '...' : name;
          const formattedSalary = salary.toLocaleString('en-IN');
          return {
            id: structure._id || structure.id || structure.structureId,
            value: salary.toString(),
            label: shortName, // Show name in dropdown
            fullLabel: `${name} - ${formattedSalary} ${currency}`, // Full label with salary for title attribute
            salary: salary,
            currency: currency,
            name: name,
            code: structure.code || ''
          };
        })
        .filter(structure => structure.salary > 0) // Only include structures with valid salary
        .sort((a, b) => b.salary - a.salary); // Sort by salary descending

      setSalaryStructures(formattedStructures);

    } catch (error) {

      setSalaryStructures([]);
    } finally {
      setLoadingSalaryStructures(false);
    }
  };

  // Load departments, locations, working hours, jobs, and salary structures on component mount
  useEffect(() => {
    loadDepartments();
    loadLocations();
    loadWorkingHours();
    loadJobs();
    loadSalaryStructures();
  }, []);

  // Repopulate form data when offer and dropdowns are loaded
  useEffect(() => {
    if (offer && departments.length > 0 && locations.length > 0 && jobs.length > 0 && salaryStructures.length > 0) {

      populateFormData(offer);
    }
  }, [offer, departments, locations, jobs, salaryStructures]);

  const populateFormData = (offerData) => {


    setFormData({
      offerType: offerData.offerType || 'full-time',
      jobTitle: offerData.jobTitle || '',
      department: offerData.department || offerData.jobDepartment || '',
      location: offerData.location || '',
      salary: offerData.salary?.toString() || '',
      currency: offerData.currency || 'INR',
      startDate: offerData.startDate ? offerData.startDate.split('T')[0] : '',
      expiryDate: offerData.expiryDate ? offerData.expiryDate.split('T')[0] : '',
      probationPeriod: offerData.probationPeriod || 6,
      noticePeriod: offerData.noticePeriod || 30,
      notes: offerData.notes || '',
      benefits: offerData.benefits || [],
      templateId: offerData.templateId || 'standard',
      terms: {
        workingHours: offerData.terms?.workingHours || ''
      }
    });

    // Set existing documents
    if (offerData.documents && Array.isArray(offerData.documents)) {
      setExistingDocuments(offerData.documents);
    }


  };

  const handleInputChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;

    // Handle job title selection - auto-populate department if available
    if (name === 'jobTitle') {
      const selectedJob = jobs.find(job => job.value === value);
      if (selectedJob && selectedJob.department) {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          department: selectedJob.department || prev.department
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    }
    // Handle salary structure selection
    else if (name === 'salary') {
      const selectedStructure = salaryStructures.find(structure => structure.value === value);
      if (selectedStructure) {
        setFormData(prev => ({
          ...prev,
          [name]: selectedStructure.salary.toString(),
          currency: selectedStructure.currency || prev.currency
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    }
    // Handle notes validation
    else if (name === 'notes') {
      // Only allow alphanumeric, spaces, and commas
      value = value.replace(/[^a-zA-Z0-9\s,]/g, '').substring(0, 1000);
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    // Handle probation and notice period
    else if (name === 'probationPeriod' || name === 'noticePeriod') {
      // Only allow numbers, ensure positive (0 or greater)
      value = value.replace(/\D/g, '');
      if (value && parseInt(value) < 0) {
        value = '0';
      }
      // Set max limits
      if (name === 'probationPeriod' && parseInt(value) > 12) {
        value = '12';
      }
      if (name === 'noticePeriod' && parseInt(value) > 365) {
        value = '365';
      }
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    // Handle other fields
    else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleTermsChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      terms: {
        ...prev.terms,
        workingHours: value
      }
    }));
  };

  const validateField = (name, value) => {
    let msg = '';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date(getISOAfterYears(MAX_YEARS_AHEAD));
    switch (name) {
      case 'startDate': {
        if (!value) { msg = 'Start date is required'; break; }
        const d = new Date(value);
        if (d < today) msg = 'Start date cannot be in the past';
        else if (d > maxDate) msg = `Start date cannot be beyond ${MAX_YEARS_AHEAD} years`;
        break;
      }
      case 'expiryDate': {
        if (!value) { msg = 'Expiry date is required'; break; }
        const d = new Date(value);
        if (d < today) msg = 'Expiry date cannot be in the past';
        else if (d > maxDate) msg = `Expiry date cannot be beyond ${MAX_YEARS_AHEAD} years`;
        else if (formData.startDate) {
          const s = new Date(formData.startDate);
          if (d <= s) msg = 'Expiry date must be after start date';
        }
        break;
      }
      case 'notes':
        if (value && !/^[a-zA-Z0-9\s,]+$/.test(value.trim())) msg = 'Notes can only contain alphanumeric characters, spaces, and commas.';
        else if (value && value.length > 1000) msg = 'Notes must be ≤ 1000 characters';
        break;
      default: break;
    }
    setErrors(prev => ({ ...prev, [name]: msg }));
    return !msg;
  };

  const handleBenefitToggle = (benefit) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.includes(benefit)
        ? prev.benefits.filter(b => b !== benefit)
        : [...prev.benefits, benefit]
    }));
  };

  const uploadToCloudinary = async (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'offer-documents');

    try {
      const response = await post(API_ENDPOINTS.UPLOAD.CLOUDINARY, formData, {
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      });

      if (response.responseCode === 200) {
        return { success: true, data: response.data };
      } else {
        return { success: false, message: response.responseMessage || 'Upload failed' };
      }
    } catch (error) {

      return {
        success: false,
        message: error.message || 'Server error during upload'
      };
    }
  };

  const deleteFromCloudinary = async (publicId, resourceType = 'raw') => {
    try {
      const response = await del(`${API_ENDPOINTS.UPLOAD.CLOUDINARY}?public_id=${publicId}&resource_type=${resourceType}`);
      return response.responseCode === 200;
    } catch (error) {

      return false;
    }
  };

  const handleDocumentSelection = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check if a document already exists (either new or existing not marked for deletion)
    const activeExistingDocs = existingDocuments.filter(doc =>
      !documentsToDelete.some(d =>
        (d.publicId && d.publicId === doc.publicId) ||
        (d._id && d._id === doc._id)
      )
    );

    if (activeExistingDocs.length > 0 || newDocuments.length > 0) {
      showError('Only one offer letter document is allowed. Please remove the existing one to upload a new one.');
      e.target.value = '';
      return;
    }

    const file = files[0];
    const maxSize = 10 * 1024 * 1024; // 10MB - matching OfferManagement
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif'
    ];

    if (file.size > maxSize) {
      showError(`${file.name} exceeds 10MB`);
      e.target.value = '';
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      showError(`${file.name} has an invalid file type`);
      e.target.value = '';
      return;
    }

    // Reset input
    e.target.value = '';

    const tempId = `upload-${Date.now()}`;

    // Add to newDocuments immediately with uploading state
    const tempDoc = {
      id: tempId,
      filename: file.name,
      originalName: file.name,
      fileSize: file.size,
      contentType: file.type,
      isUploading: true
    };

    setNewDocuments([tempDoc]);
    setUploadingDocuments(true);
    setIsUploading(true);
    setUploadProgress(prev => ({ ...prev, [tempId]: 0 }));

    const result = await uploadToCloudinary(file, (progress) => {
      const displayProgress = Math.min(progress, 98);
      setUploadProgress(prev => ({ ...prev, [tempId]: displayProgress }));
    });

    if (result.success) {
      setUploadProgress(prev => ({ ...prev, [tempId]: 100 }));

      const newDocMetadata = {
        id: tempId,
        filename: file.name,
        originalName: file.name,
        fileUrl: result.data.url,
        publicId: result.data.public_id,
        resourceType: result.data.resource_type || 'raw',
        fileSize: file.size,
        contentType: file.type,
        uploadedAt: new Date()
      };

      setNewDocuments([newDocMetadata]);
      showSuccess(`Document "${file.name}" uploaded successfully`);

      setTimeout(() => {
        setUploadProgress(prev => {
          const updated = { ...prev };
          delete updated[tempId];
          return updated;
        });
      }, 1000);
    } else {
      showError(`Failed to upload "${file.name}": ${result.message}`);
    }

    setUploadingDocuments(false);
    setIsUploading(false);
  };

  const removeExistingDocument = (index) => {
    const docToRemove = existingDocuments[index];
    // Only add to delete list, don't remove from UI
    setDocumentsToDelete(prev => [...prev, docToRemove]);
  };

  const removeNewDocument = async (index) => {
    const docToRemove = newDocuments[index];
    if (docToRemove && docToRemove.publicId) {
      setUploadingDocuments(true);
      await deleteFromCloudinary(docToRemove.publicId, docToRemove.resourceType || 'raw');
      setUploadingDocuments(false);
    }
    setNewDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};

    // Job Title validation
    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = 'Job title is required';
    }

    // Department validation
    if (!formData.department.trim()) {
      newErrors.department = 'Department is required';
    }

    // Location validation
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    // Salary validation
    const salaryNum = parseInt(formData.salary) || 0;
    if (!formData.salary || salaryNum <= 0) {
      newErrors.salary = 'Valid salary is required';
    }

    // Date validations with max horizon
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const maxDate = new Date(getISOAfterYears(MAX_YEARS_AHEAD));
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    else {
      const s = new Date(formData.startDate);
      if (s < today) newErrors.startDate = 'Start date cannot be in the past';
      else if (s > maxDate) newErrors.startDate = `Start date cannot be beyond ${MAX_YEARS_AHEAD} years`;
    }
    if (!formData.expiryDate) newErrors.expiryDate = 'Expiry date is required';
    else {
      const d = new Date(formData.expiryDate);
      if (d < today) newErrors.expiryDate = 'Expiry date cannot be in the past';
      else if (d > maxDate) newErrors.expiryDate = `Expiry date cannot be beyond ${MAX_YEARS_AHEAD} years`;
    }
    if (formData.startDate && formData.expiryDate) {
      if (new Date(formData.expiryDate) <= new Date(formData.startDate)) newErrors.expiryDate = 'Expiry date must be after start date';
    }

    // Text limits
    if (formData.notes && !/^[a-zA-Z0-9\s,]+$/.test(formData.notes.trim())) {
      newErrors.notes = 'Notes can only contain alphanumeric characters, spaces, and commas.';
    } else if (formData.notes && formData.notes.length > 1000) {
      newErrors.notes = 'Notes must be ≤ 1000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showError('Please fix the validation errors before submitting. All required fields must be filled correctly.');
      return;
    }

    // Check if all required fields are filled
    const requiredFields = ['jobTitle', 'department', 'location', 'salary', 'startDate', 'expiryDate'];
    const missingFields = requiredFields.filter(field => {
      return !formData[field] || formData[field].toString().trim() === '';
    });

    if (missingFields.length > 0) {
      showError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    setSaving(true);
    setUploadingDocuments(true);

    try {


      // Collect all documents: existing (minus those marked for deletion) + newly uploaded
      // IMPORTANT: Strip 'id' and 'isUploading' fields before sending to backend
      // to avoid Mongoose CastError (as 'id' is treated as virtual '_id')
      const finalDocuments = [
        ...existingDocuments.filter(doc =>
          !documentsToDelete.some(d =>
            (d.publicId && d.publicId === doc.publicId) ||
            (d._id && d._id === doc._id)
          )
        ),
        ...newDocuments
      ].map(({ id, isUploading, ...doc }) => doc);

      // Prepare payload as JSON
      const payload = {
        ...formData,
        documents: finalDocuments,
        documentsToDelete: documentsToDelete.map(doc => doc._id || doc.publicId).filter(Boolean)
      };

      const result = await offerService.updateOffer(id, payload);

      if (result && result.responseCode === 200) {


        let successMessage = 'Offer updated successfully!';
        if (newDocuments.length > 0) {
          successMessage += ` ${newDocuments.length} document(s) uploaded.`;
        }
        if (documentsToDelete.length > 0) {
          successMessage += ` ${documentsToDelete.length} document(s) removed.`;
        }

        showSuccess(successMessage);
        navigate('/offer_management');
      } else {
        showError(result?.responseMessage || 'Failed to update offer');
      }
    } catch (error) {

      showError('Failed to update offer. Please try again.');
    } finally {
      setSaving(false);
      setUploadingDocuments(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-agrosaf-green mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading offer details...</p>
        </div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Offer Not Found</h2>
          <p className="text-gray-600 mb-4">The offer you're looking for doesn't exist.</p>
          <Button
            onClick={() => navigate('/offer_management')}
            variant="primary"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Offers
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/offer_management')}
              variant="outline"
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Edit Offer</h1>
              <p className="text-gray-600">Update offer details for {offer.candidateName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
              {offer.offerId}
            </span>
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${offer.status === 'draft' ? 'bg-gray-100 text-gray-800' :
              offer.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                offer.status === 'accepted' ? 'bg-green-100 text-green-800' :
                  offer.status === 'rejected' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
              }`}>
              {offer.status?.charAt(0).toUpperCase() + offer.status?.slice(1)}
            </span>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Offer Details
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Job Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Title *
              </label>
              <select
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-0 focus:border-agrosaf-green ${errors.jobTitle ? 'border-red-500' : 'border-gray-300'
                  }`}
                disabled={loadingJobs}
                required
              >
                <option value="">
                  {loadingJobs ? 'Loading jobs...' : 'Select job title'}
                </option>
                {formData.jobTitle && !hasCurrentJobTitleOption && (
                  <option value={formData.jobTitle}>
                    {formData.jobTitle} (current)
                  </option>
                )}
                {jobs.length === 0 && !loadingJobs ? (
                  <option value="" disabled>
                    No active jobs available
                  </option>
                ) : (
                  jobs.map((job) => (
                    <option key={job.id} value={job.value}>
                      {job.label}
                    </option>
                  ))
                )}
              </select>
              {errors.jobTitle && (
                <p className="mt-1 text-sm text-red-600">{errors.jobTitle}</p>
              )}
            </div>

            {/* Department */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Department *
              </label>
              <select
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-0 focus:border-agrosaf-green ${errors.department ? 'border-red-500' : 'border-gray-300'
                  }`}
                disabled={loadingDepartments}
              >
                <option value="">
                  {loadingDepartments ? 'Loading departments...' : 'Select department'}
                </option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.value}>
                    {dept.label}
                  </option>
                ))}
              </select>
              {errors.department && (
                <p className="mt-1 text-sm text-red-600">{errors.department}</p>
              )}
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location *
              </label>
              <select
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-0 focus:border-agrosaf-green ${errors.location ? 'border-red-500' : 'border-gray-300'
                  }`}
                disabled={loadingLocations}
              >
                <option value="">
                  {loadingLocations ? 'Loading locations...' : 'Select location'}
                </option>
                {locations.map((location) => (
                  <option key={location.code} value={location.value}>
                    {location.label} {location.city && location.state ? `(${location.city}, ${location.state})` : ''}
                  </option>
                ))}
              </select>
              {errors.location && (
                <p className="mt-1 text-sm text-red-600">{errors.location}</p>
              )}
            </div>

            {/* Offer Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Offer Type *
              </label>
              <select
                name="offerType"
                value={formData.offerType}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-0 focus:border-agrosaf-green"
              >
                {offerTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Salary */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Salary Structure *
              </label>
              <select
                name="salary"
                value={formData.salary}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-0 focus:border-agrosaf-green ${errors.salary ? 'border-red-500' : 'border-gray-300'
                  }`}
                disabled={loadingSalaryStructures}
                required
              >
                <option value="">
                  {loadingSalaryStructures ? 'Loading salary structures...' : 'Select salary structure'}
                </option>
                {formData.salary && !hasCurrentSalaryOption && (
                  <option value={formData.salary} title={`${parseInt(formData.salary).toLocaleString('en-IN')} INR (current)`}>
                    {parseInt(formData.salary).toLocaleString('en-IN')} INR (current)
                  </option>
                )}
                {salaryStructures.length === 0 && !loadingSalaryStructures ? (
                  <option value="" disabled>
                    No salary structures available
                  </option>
                ) : (
                  salaryStructures.map((structure) => (
                    <option key={structure.id} value={structure.value} title={structure.fullLabel}>
                      {structure.label}
                    </option>
                  ))
                )}
              </select>
              {errors.salary && (
                <p className="mt-1 text-sm text-red-600">{errors.salary}</p>
              )}
              {formData.salary && (
                <p className="text-xs text-gray-600 mt-1">
                  Selected CTC:{' '}
                  <span className="font-semibold text-gray-900">
                    {parseInt(formData.salary).toLocaleString('en-IN')} INR
                  </span>
                </p>
              )}
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date *
              </label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                onBlur={(e) => validateField('startDate', e.target.value)}
                min={getTodayISO()}
                max={getISOAfterYears(MAX_YEARS_AHEAD)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-0 focus:border-agrosaf-green ${errors.startDate ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {errors.startDate && (
                <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>
              )}
            </div>

            {/* Expiry Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expiry Date *
              </label>
              <input
                type="date"
                name="expiryDate"
                value={formData.expiryDate}
                onChange={handleInputChange}
                onBlur={(e) => validateField('expiryDate', e.target.value)}
                min={formData.startDate || getTodayISO()}
                max={getISOAfterYears(MAX_YEARS_AHEAD)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-0 focus:border-agrosaf-green ${errors.expiryDate ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {errors.expiryDate && (
                <p className="mt-1 text-sm text-red-600">{errors.expiryDate}</p>
              )}
            </div>

            {/* Probation Period */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Probation Period (Months)
              </label>
              <select
                name="probationPeriod"
                value={formData.probationPeriod}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-0 focus:border-agrosaf-green"
              >
                <option value="">Select Probation Period</option>
                <option value="0">0 months</option>
                <option value="1">1 month</option>
                <option value="2">2 months</option>
                <option value="3">3 months</option>
                <option value="4">4 months</option>
                <option value="5">5 months</option>
                <option value="6">6 months</option>
              </select>
            </div>

            {/* Notice Period */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notice Period
              </label>
              <select
                name="noticePeriod"
                value={formData.noticePeriod}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-0 focus:border-agrosaf-green"
              >
                <option value="">Select Notice Period</option>
                <option value="immediate">Immediate</option>
                <option value="15-days">15 days</option>
                <option value="30-days">30 days</option>
                <option value="60-days">60 days</option>
                <option value="90-days">90 days</option>
              </select>
            </div>

            {/* Shift */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shift *
              </label>
              <select
                name="workingHours"
                value={formData.terms.workingHours}
                onChange={handleTermsChange}
                disabled={loadingWorkingHours}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-0 focus:border-agrosaf-green"
              >
                <option value="">
                  {loadingWorkingHours ? 'Loading shifts...' : 'Select shift'}
                </option>
                {formData.terms.workingHours && !hasCurrentWorkingHoursOption && (
                  <option value={formData.terms.workingHours}>
                    {formData.terms.workingHours} (current)
                  </option>
                )}
                {workingHoursOptions.length === 0 && !loadingWorkingHours ? (
                  <option value="" disabled>
                    No active shifts available
                  </option>
                ) : (
                  workingHoursOptions.map(option => (
                    <option key={option.id || option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                )}
              </select>
              {formData.terms.workingHours && (
                <p className="text-xs text-gray-600 mt-1">
                  Selected Shift: <span className="font-semibold text-gray-900">{formData.terms.workingHours}</span>
                </p>
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              onKeyPress={(e) => {
                // Only allow alphanumeric, spaces, and commas
                if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              onBlur={(e) => validateField('notes', e.target.value)}
              maxLength={1000}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-0 focus:border-agrosaf-green"
              placeholder="Enter any additional notes..."
            />
            <div className="mt-1 text-xs text-gray-500">{formData.notes?.length || 0}/1000 characters</div>
            {errors.notes && (
              <p className="mt-1 text-sm text-red-600">{errors.notes}</p>
            )}
          </div>
        </div>

        {/* Benefits */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Benefits
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {availableBenefits.map(benefit => (
              <label key={benefit} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.benefits.includes(benefit)}
                  onChange={() => handleBenefitToggle(benefit)}
                  className="h-4 w-4 text-brand focus:ring-0 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">{benefit}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Offer Letter Documents */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Offer Letter Documents
          </h2>
          {/* Document Upload Area - Hide if a document is already present */}
          {(existingDocuments.filter(doc =>
            !documentsToDelete.some(d =>
              (d.publicId && d.publicId === doc.publicId) ||
              (d._id && d._id === doc._id)
            )
          ).length === 0 && newDocuments.length === 0) && (
              <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${uploadingDocuments
                ? 'border-blue-300 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
                }`}>
                <input
                  type="file"
                  id="documentUpload"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                  onChange={handleDocumentSelection}
                  className="hidden"
                  disabled={uploadingDocuments}
                />
                <label
                  htmlFor="documentUpload"
                  className={`flex flex-col items-center ${uploadingDocuments ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                    }`}
                >
                  <div className="bg-blue-50 p-3 rounded-full mb-3 group-hover:bg-blue-100 transition-colors">
                    <FileText className="h-8 w-8 text-blue-500" />
                  </div>
                  <p className="text-sm font-medium text-gray-900 mb-1">
                    Click to select offer letter
                  </p>
                  <p className="text-xs text-gray-500 mb-3">
                    PDF, Word, or Images (max 2MB)
                  </p>
                  <div className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 shadow-sm hover:bg-blue-50/30 transition-colors">
                    Choose File
                  </div>
                </label>
              </div>
            )}

          {/* Existing Documents Display */}
          {existingDocuments.length > 0 && (
            <div className="mt-4 space-y-3">
              <h4 className="text-sm font-medium text-gray-700">Existing Offer Letter</h4>
              {existingDocuments.map((doc, index) => {
                const isMarkedForDeletion = documentsToDelete.some(
                  d => (d.publicId && d.publicId === doc.publicId) ||
                    (d._id && d._id === doc._id)
                );

                return (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-4 rounded-xl transition-all duration-200 border ${isMarkedForDeletion
                      ? 'bg-red-50 border-red-200 opacity-60'
                      : 'bg-blue-50 border-blue-100 hover:shadow-md'
                      }`}
                  >
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className={`p-2 rounded-lg bg-white border ${isMarkedForDeletion ? 'border-red-100' : 'border-blue-100'}`}>
                        <FileText className={`h-5 w-5 ${isMarkedForDeletion ? 'text-red-400' : 'text-blue-500'}`} />
                      </div>
                      <div className="min-w-0">
                        {doc.fileUrl && !isMarkedForDeletion ? (
                          <a
                            href={doc.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-semibold text-gray-900 hover:text-[#0F2942] transition-colors flex items-center gap-1 group"
                          >
                            <span className="truncate">{doc.originalName || doc.filename || `Offer Letter`}</span>
                            <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </a>
                        ) : (
                          <p className={`text-sm font-semibold truncate ${isMarkedForDeletion ? 'text-red-700 line-through' : 'text-gray-900'}`}>
                            {doc.originalName || doc.filename || `Offer Letter`}
                          </p>
                        )}
                        <p className={`text-xs mt-0.5 ${isMarkedForDeletion ? 'text-red-500' : 'text-gray-500'}`}>
                          {doc.fileSize ? `${(doc.fileSize / 1024).toFixed(1)} KB` : 'Cloud Document'}
                          {isMarkedForDeletion && <span className="ml-2 font-medium font-italic">• Will be removed on save</span>}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        if (isMarkedForDeletion) {
                          setDocumentsToDelete(prev => prev.filter(
                            d => !(
                              (d.publicId && d.publicId === doc.publicId) ||
                              (d._id && d._id === doc._id)
                            )
                          ));
                        } else {
                          removeExistingDocument(index);
                        }
                      }}
                      className={`p-2 rounded-lg transition-colors ${isMarkedForDeletion
                        ? 'text-green-600 hover:bg-green-100'
                        : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                        }`}
                      title={isMarkedForDeletion ? 'Undo removal' : 'Remove document'}
                    >
                      {isMarkedForDeletion ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* New Document Display (Pending Upload) */}
          {newDocuments.length > 0 && (
            <div className="mt-4 space-y-3">
              <h4 className="text-sm font-medium text-gray-700">New Offer Letter</h4>
              {newDocuments.map((file, index) => (
                <div key={index} className={`flex items-center justify-between p-4 rounded-xl transition-all duration-200 border ${file.isUploading ? 'bg-green-50/30 border-green-100' : 'bg-green-50 border-green-100 hover:shadow-md'}`}>
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <div className={`p-2 rounded-lg bg-white border ${file.isUploading ? 'border-green-50' : 'border-green-100'}`}>
                      {file.isUploading ? (
                        <Loader2 className="h-5 w-5 text-green-500 animate-spin" />
                      ) : (
                        <FileText className="h-5 w-5 text-green-500" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {file.name || file.filename || file.originalName}
                      </p>
                      <div className="flex items-center mt-0.5">
                        <p className="text-xs text-green-600 font-medium whitespace-nowrap">
                          {file.fileSize ? `${(file.fileSize / 1024).toFixed(1)} KB` : 'Uploading...'}
                        </p>
                        {file.isUploading && (
                          <span className="ml-2 text-[10px] font-bold text-green-700 uppercase tracking-wider">
                            {uploadProgress[file.id] >= 98 ? 'Finalizing...' : `Uploading ${uploadProgress[file.id] || 0}%`}
                          </span>
                        )}
                        {!file.isUploading && (
                          <span className="ml-2 text-[10px] font-bold text-green-700 uppercase tracking-wider">
                            Ready to save
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center ml-4">
                    {file.isUploading ? (
                      <div className="w-8 h-8 relative">
                        <svg className="w-full h-full -rotate-90">
                          <circle cx="16" cy="16" r="14" fill="none" stroke="#e2e8f0" strokeWidth="2.5" />
                          <circle
                            cx="16"
                            cy="16"
                            r="14"
                            fill="none"
                            stroke="#10b981"
                            strokeWidth="2.5"
                            strokeDasharray="87.96"
                            strokeDashoffset={87.96 - (87.96 * (uploadProgress[file.id] || 0)) / 100}
                            className="transition-all duration-300 ease-out"
                          />
                        </svg>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => removeNewDocument(index)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove file"
                      >
                        <XCircle className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            onClick={() => navigate('/offer_management')}
            variant="outline"
            className="flex items-center gap-2"
          >
            <XCircle className="h-4 w-4" />
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving}
            className="flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditOffer;